import { calcularInventario } from '../services/valoracion';
import {
  getDefaultExportDateFrom,
  getDefaultExportDateTo,
  isDateInRange,
  toLocalDateKey,
} from '../../retail-pos/utils/exportRetailData';

export { getDefaultExportDateFrom, getDefaultExportDateTo };

export const MOVEMENT_FILTER = {
  ALL: 'TODOS',
  COMPRAS: 'COMPRAS',
  VENTAS: 'VENTAS',
  SALIDAS: 'SALIDAS',
};

export const MOVEMENT_FILTER_LABELS = {
  [MOVEMENT_FILTER.ALL]: 'Todos los tipos',
  [MOVEMENT_FILTER.COMPRAS]: 'Compras',
  [MOVEMENT_FILTER.VENTAS]: 'Ventas',
  [MOVEMENT_FILTER.SALIDAS]: 'Salidas',
};

export function createDefaultInventarioFilters() {
  return {
    dateFrom: getDefaultExportDateFrom(),
    dateTo: getDefaultExportDateTo(),
    productId: 'TODOS',
    movementType: MOVEMENT_FILTER.ALL,
  };
}

export function createDefaultStockProductFilter() {
  return { productId: 'TODOS' };
}

export function filterProductosById(productos = [], productId = 'TODOS') {
  if (productId === 'TODOS') return productos || [];
  return (productos || []).filter((p) => p.id === productId);
}

export function classifyMovement(m) {
  if (!m || m.anulado) return null;
  if (m.tipo === 'ENTRADA') return MOVEMENT_FILTER.COMPRAS;
  if (m.tipo === 'VENTA' || (m.tipo === 'SALIDA' && m.subTipo === 'VENTA')) {
    return MOVEMENT_FILTER.VENTAS;
  }
  if (m.tipo === 'SALIDA') return MOVEMENT_FILTER.SALIDAS;
  return null;
}

export function getMovementLabel(m) {
  if (m?.tipo === 'ENTRADA') return 'Compra';
  if (m?.tipo === 'VENTA' || m?.subTipo === 'VENTA') return 'Venta';
  if (m?.tipo === 'SALIDA') return m.subTipo || 'Salida';
  return m?.tipo || '';
}

export function isDateOnOrBefore(fecha, dateTo) {
  const key = toLocalDateKey(fecha);
  if (!key) return false;
  if (dateTo && key > dateTo) return false;
  return true;
}

export function filterMovimientos(movimientos = [], filters = {}) {
  const {
    dateFrom = getDefaultExportDateFrom(),
    dateTo = getDefaultExportDateTo(),
    productId = 'TODOS',
    movementType = MOVEMENT_FILTER.ALL,
  } = filters;

  return (movimientos || []).filter((m) => {
    if (productId !== 'TODOS' && m.productoId !== productId) return false;
    if (!isDateInRange(m.fecha, dateFrom, dateTo)) return false;
    if (movementType !== MOVEMENT_FILTER.ALL) {
      if (classifyMovement(m) !== movementType) return false;
    }
    return true;
  });
}

export function calcularStockHasta(movimientos, productoId, dateTo) {
  const movs = (movimientos || []).filter(
    (m) => m.productoId === productoId && !m.anulado && isDateOnOrBefore(m.fecha, dateTo)
  );
  const entradas = movs
    .filter((m) => m.tipo === 'ENTRADA')
    .reduce((acc, m) => acc + (parseFloat(m.cantidad) || 0), 0);
  const salidas = movs
    .filter((m) => m.tipo === 'SALIDA' || m.tipo === 'VENTA')
    .reduce((acc, m) => acc + (parseFloat(m.cantidad) || 0), 0);
  return entradas - salidas;
}

export function obtenerPrecioPromedioHasta(movimientos, productoId, dateTo, metodo = 'PROMEDIO') {
  const hasta = (movimientos || []).filter(m => isDateOnOrBefore(m.fecha, dateTo));
  return calcularInventario(hasta, metodo).saldos[String(productoId)]?.promedio || 0;
}

export function productIdsWithMovementInRange(movimientos, filters) {
  const filtered = filterMovimientos(movimientos, filters);
  return new Set(filtered.map((m) => m.productoId));
}

export function filterProductosForInventario(productos = [], movimientos = [], filters = {}) {
  const {
    productId = 'TODOS',
    movementType = MOVEMENT_FILTER.ALL,
  } = filters;

  let list = [...productos];
  if (productId !== 'TODOS') {
    list = list.filter((p) => p.id === productId);
  }
  if (movementType !== MOVEMENT_FILTER.ALL) {
    const ids = productIdsWithMovementInRange(movimientos, filters);
    list = list.filter((p) => ids.has(p.id));
  }
  return list;
}

export function summarizeMovementsInRange(movimientos, productoId, filters) {
  const filtered = filterMovimientos(movimientos, { ...filters, productId: productoId || 'TODOS' });
  return filtered.reduce(
    (acc, m) => {
      if (m.anulado) return acc;
      const qty = parseFloat(m.cantidad) || 0;
      const kind = classifyMovement(m);
      if (kind === MOVEMENT_FILTER.COMPRAS) acc.compras += qty;
      else if (kind === MOVEMENT_FILTER.VENTAS) acc.ventas += qty;
      else if (kind === MOVEMENT_FILTER.SALIDAS) acc.salidas += qty;
      return acc;
    },
    { compras: 0, ventas: 0, salidas: 0 }
  );
}

export function filtersDateRangeInvalid(filters) {
  const { dateFrom, dateTo } = filters || {};
  return Boolean(dateFrom && dateTo && dateFrom > dateTo);
}
